import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import { usePatientRiskGapsWithUIExclusions } from '@ctw/shared/api/fhir/risk-gaps';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { EmptyPatientTable } from '@ctw/shared/content/empty-patients-table';
import { PatientRiskGapsResourceTable } from '@ctw/shared/content/patient-risk-gaps/components/resource-table';
import { buildPatientRiskGapsColumnsV2 } from '@ctw/shared/content/patient-risk-gaps/helpers/columns-v2';
import { PeriodDropdown } from '@ctw/shared/content/patient-risk-gaps/helpers/period-dropdown';
import { useSearchParamsStateForRiskGaps } from '@ctw/shared/content/patient-risk-gaps/helpers/risk-gaps-search-params';
import {
  ListDetailViewWithDockableDrawer,
  useHasListDetailViewWithDockableDrawer,
  useListDetailViewDetailPanel,
  useListDetailViewHeader,
} from '@ctw/shared/content/patient-risk-gaps/layout/list-detail-view-with-dockable-drawer';
import { ResourceTableActions } from '@ctw/shared/content/resource/resource-table-actions';
import { RiskGapDetailView } from '@ctw/shared/content/risk/risk-gap-detail-view';
import { useFilteredSortedData } from '@ctw/shared/hooks/use-filtered-sorted-data';
import { useMemo, useState } from 'react';
import { defaultRiskSort } from './helpers/sorts';

interface PatientRiskGapsV1Props {
  testId?: string;
}

function PatientRiskGapsV1Content({ testId }: { testId?: string }) {
  const risksQuery = usePatientRiskGapsWithUIExclusions();
  const [selectedRiskGapId, setSelectedRiskGapId] = useSearchParamsStateForRiskGaps();

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString());

  const patientRiskGapsColumns = useMemo(() => buildPatientRiskGapsColumnsV2(), []);

  const riskGapsFilteredByYear = useMemo(() => {
    if (!risksQuery.data) {
      return [];
    }
    return risksQuery.data.filter((riskGap) => {
      const periodYear = riskGap.period;
      return periodYear === selectedYear;
    });
  }, [risksQuery.data, selectedYear]);

  const { data, filters, setFilters } = useFilteredSortedData({
    cacheKey: 'patient-risk-gaps',
    defaultSort: defaultRiskSort,
    records: riskGapsFilteredByYear,
  });
  const selectedIndex = useMemo(
    () => data?.findIndex(({ id }) => id === selectedRiskGapId),
    [selectedRiskGapId, data],
  );

  const hasZeroFilteredRecords = !risksQuery.isLoading && data.length === 0;
  const selectedRiskGap = useMemo<RiskGapModel | null>(() => {
    if (!selectedRiskGapId) {
      return null;
    }
    return (risksQuery.data ?? []).find((riskGap) => riskGap.id === selectedRiskGapId) ?? null;
  }, [risksQuery.data, selectedRiskGapId]);

  useListDetailViewDetailPanel(
    selectedRiskGap ? (
      <RiskGapDetailView
        riskGap={selectedRiskGap}
        onClose={() => {
          setSelectedRiskGapId(null);
        }}
      />
    ) : null,
    [selectedRiskGap],
  );

  const filtersKey = JSON.stringify(filters);
  useListDetailViewHeader(
    () => (
      <ResourceTableActions
        headAction={<PeriodDropdown selectedYear={selectedYear} onChange={setSelectedYear} />}
        filterOptions={{
          filters: [],
          selected: filters,
          onChange: setFilters,
        }}
        className="w-full justify-between p-1"
      />
    ),
    [filtersKey, selectedYear],
  );

  return (
    <PatientRiskGapsResourceTable
      title="Patient Risk Gaps"
      titleVisuallyHidden={false}
      showTableHead={true}
      isLoading={risksQuery.isLoading}
      stackedBreakpoint="md"
      hasSelectedRow={selectedRiskGap !== null}
      selectedIndex={selectedIndex}
      columns={patientRiskGapsColumns}
      data={data}
      testId={testId}
      emptyMessage={
        <EmptyPatientTable
          hasZeroFilteredRecords={hasZeroFilteredRecords}
          resourceName="risk opportunities"
        />
      }
      onRowClick={({ record }) => {
        setSelectedRiskGapId(record.id);
      }}
    />
  );
}

export const PatientRiskGapsV1 = withErrorBoundary<PatientRiskGapsV1Props>({
  boundaryName: 'PatientRiskGaps',
  Component: ({ testId }: PatientRiskGapsV1Props) => {
    const hasListDetailView = useHasListDetailViewWithDockableDrawer();
    if (hasListDetailView) {
      return <PatientRiskGapsV1Content testId={testId} />;
    }
    return (
      <ListDetailViewWithDockableDrawer>
        <PatientRiskGapsV1Content testId={testId} />
      </ListDetailViewWithDockableDrawer>
    );
  },
});
